import { checkSession } from "@features/auth";
import { getUserOrganizationId, useUserInfo } from "@features/auth/useAuth";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Button } from "@shadcn/ui/button";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Plus } from "lucide-react";
import { type PreloadedQuery, usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { z } from "zod";
import { AnonymousLockAccess, AnonymousLockAccessError } from "~/components/lock/AnonymousLockAccess";
import { MyLocksTabs } from "~/components/lock/MyLocks/MyLocksTabs";
import { ServiceBreadcrumb } from "~/components/lock/ServiceBreadcrumb";
import type { MyLocksTabsFragment$key } from "~/components/lock/MyLocks/__generated__/MyLocksTabsFragment.graphql";
import { useLockOpen } from "~/components/lock/MyLocks/useLockOpen";
import { useReservationLockOpen } from "~/components/lock/MyLocks/useReservationLockOpen";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import type { anonymousMyLockQuery } from "./__generated__/anonymousMyLockQuery.graphql";
import type { myLocksListQuery } from "./__generated__/myLocksListQuery.graphql";

export const query = graphql`
	query myLocksListQuery($hubName: String!, $skipOwnedReservations: Boolean!) @relay_test_operation {
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			name
			hub @required(action: THROW) {
				id
				status
			}
		}
		myUtilizationGrants(input: {}, pagination: { page: 0, pageSize: 1000 }) {
			nodes {
				reservation {
					flow
				}
				...MyLocksTabsFragment
			}
		}
		b2BLockReservations: lockReservations(
			filter: { subtype: B2B, flow: SWAP }
			orderBy: [{ field: STARTED_AT, ordering: DESC }]
			pagination: { page: 0, pageSize: 1000 }
		) @skip(if: $skipOwnedReservations) {
			nodes {
				id
				...MyLocksTabsOwnedFragment
			}
		}
	}
`;

const anonymousQuery = graphql`
	query anonymousMyLockQuery($accessToken: String!) @relay_test_operation {
		anonymousUtilizationGrant(accessToken: $accessToken) {
			...AnonymousLockAccessFragment
			...MyLocksTabsFragment
		}
	}
`;

type AnonymousLoaderData = {
	kind: "anonymous";
	token: string;
	queryRef: PreloadedQuery<anonymousMyLockQuery>;
};

type AuthenticatedLoaderData = {
	kind: "authenticated";
	queryRef: PreloadedQuery<myLocksListQuery>;
	token?: string;
	anonymousQueryRef?: PreloadedQuery<anonymousMyLockQuery>;
};

export const Route = createFileRoute("/$hubMetaName/swap/my")({
	beforeLoad: () => {
		document.title = "bacehub | Lock";
	},
	component: RouteComponent,
	errorComponent: ({ error }) => <AnonymousLockAccessError error={error} service="LOCK_B2B" />,
	validateSearch: zodValidator(
		z.object({
			token: z.string().optional(),
			session: z.string().uuid().optional(),
		})
	),
	loader: async (ctx): Promise<AnonymousLoaderData | AuthenticatedLoaderData> => {
		const token = new URLSearchParams(ctx.location.searchStr).get("token");
		const sessionExists = await checkSession();
		if (token && (!sessionExists || !getUserOrganizationId())) {
			return {
				kind: "anonymous",
				token,
				queryRef: await preloadQuery<anonymousMyLockQuery>(ctx.context.relayEnvironment, anonymousQuery, {
					accessToken: token,
				}),
			};
		}

		if (!sessionExists) {
			throw redirect({
				to: "/auth",
				search: { redirectToPath: `/${ctx.params.hubMetaName}/swap/my${ctx.location.searchStr}` },
			});
		}
		return {
			kind: "authenticated",
			queryRef: await preloadQuery<myLocksListQuery>(ctx.context.relayEnvironment, query, {
				hubName: ctx.params.hubMetaName,
				skipOwnedReservations: !getUserOrganizationId(),
			}),
			token: token ?? undefined,
			anonymousQueryRef: token
				? await preloadQuery<anonymousMyLockQuery>(ctx.context.relayEnvironment, anonymousQuery, { accessToken: token })
				: undefined,
		};
	},
});

function RouteComponent() {
	const loaderData = Route.useLoaderData();
	if (loaderData.kind === "anonymous") return <AnonymousLocks loaderData={loaderData} />;

	return <AuthenticatedLocks loaderData={loaderData} />;
}

function AnonymousLocks(props: { loaderData: AnonymousLoaderData }) {
	const { anonymousUtilizationGrant } = usePreloadedQuery<anonymousMyLockQuery>(anonymousQuery, props.loaderData.queryRef);
	return <AnonymousLockAccess token={props.loaderData.token} grant={anonymousUtilizationGrant ?? undefined} />;
}

function AuthenticatedLocks(props: { loaderData: AuthenticatedLoaderData }) {
	if (props.loaderData.anonymousQueryRef && props.loaderData.token) {
		return (
			<AuthenticatedLocksWithToken
				loaderData={props.loaderData}
				anonymousQueryRef={props.loaderData.anonymousQueryRef}
				token={props.loaderData.token}
			/>
		);
	}

	return <AuthenticatedLocksContent loaderData={props.loaderData} />;
}

function AuthenticatedLocksWithToken(props: {
	loaderData: AuthenticatedLoaderData;
	anonymousQueryRef: PreloadedQuery<anonymousMyLockQuery>;
	token: string;
}) {
	const { anonymousUtilizationGrant } = usePreloadedQuery<anonymousMyLockQuery>(anonymousQuery, props.anonymousQueryRef);
	return (
		<AuthenticatedLocksContent loaderData={props.loaderData} anonymousGrant={anonymousUtilizationGrant} anonymousToken={props.token} />
	);
}

function AuthenticatedLocksContent(props: {
	loaderData: AuthenticatedLoaderData;
	anonymousGrant?: MyLocksTabsFragment$key[number] | null;
	anonymousToken?: string;
}) {
	const navigate = useNavigate();
	const { session: highlightedSessionId } = Route.useSearch();
	const me = useUserInfo();
	const i18n = useI18nContext().LL["new-ui"];
	const { hubMeta, myUtilizationGrants, b2BLockReservations } = usePreloadedQuery<myLocksListQuery>(query, props.loaderData.queryRef);

	const lockOpen = useLockOpen("LOCK_B2B");
	const ownedLockOpen = useReservationLockOpen("LOCK_B2B");

	if (lockOpen.overlay) return lockOpen.overlay;
	if (ownedLockOpen.overlay) return ownedLockOpen.overlay;

	return (
		<PageContainer
			footer={
				me?.organization ? (
					<Button
						className="w-full shadow-xl"
						size="lg"
						onClick={() => navigate({ to: "/$hubMetaName/swap/select", params: { hubMetaName: hubMeta.name } })}
					>
						<Plus className="size-4" />
						<T>{i18n.LOCK_B2B_MANAGE_COMPARTMENTS()}</T>
					</Button>
				) : null
			}
		>
			<AppHeader service="LOCK_B2B" />
			<div className="flex flex-col gap-4 px-6 pt-6">
				<div className="flex flex-col gap-2">
					<ServiceBreadcrumb hubMetaName={hubMeta.name} current={i18n.HOME_LOCK_MANAGED_TITLE()} />
					<h1 className="text-2xl font-breit font-medium">
						<T>{i18n.LOCK_B2B_MY_COMPARTMENTS_TITLE()}</T>
					</h1>
				</div>

				<MyLocksTabs
					grants={myUtilizationGrants.nodes.filter((grant) => grant.reservation.flow === "SWAP")}
					anonymousGrant={props.anonymousGrant}
					anonymousToken={props.anonymousToken}
					ownedReservations={b2BLockReservations?.nodes ?? []}
					hubId={hubMeta.hub.id}
					userId={me?.id}
					isOpening={lockOpen.isOpening || ownedLockOpen.isOpening}
					openedGrantId={lockOpen.openedGrantId}
					onOpen={lockOpen.requestOpen}
					onEndRental={(grant) =>
						navigate({
							to: "/$hubMetaName/swap/end-rental/$reservationId",
							params: { hubMetaName: hubMeta.name, reservationId: grant.reservation.id },
						})
					}
					onOpenOwned={(reservation) => ownedLockOpen.requestOpen(reservation.id)}
					onEndOwnedRental={(reservation) =>
						navigate({
							to: "/$hubMetaName/swap/end-rental/$reservationId",
							params: { hubMetaName: hubMeta.name, reservationId: reservation.id },
							search: { source: reservation.subtype.__typename === "B2ClockReservationSubtype" ? "b2c" : "owner" },
						})
					}
					openedReservationId={ownedLockOpen.openedReservationId}
					highlightedSessionId={highlightedSessionId}
					subtype="B2B"
				/>
			</div>

			{lockOpen.confirmDialog}
			{ownedLockOpen.confirmDialog}
		</PageContainer>
	);
}
